import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  defer,
  delay,
  iif,
  map,
  of,
  switchMap,
  throwError
} from 'rxjs';
import { environment } from '@environments/environment';
import { Session } from '@providers/session/session.service';
import { Chat, WSChatMessageRequest } from './chat.request.interface';

/**
 * Service for connecting with chat's websocket.
 */
@Injectable()
export class ApiChatWebsocketService {
  private readonly session = inject(Session);
  maxRetries = 10;
  params: {
    id: string | null;
  } = {
    id: null
  };
  retries = 0;
  ws: WebSocket | null = null;
  channelChatReceived = new Subject<Chat>();

  channelRunOutQuota = new Subject<void>();
  wsMessageReceived = new Subject<WSChatMessageRequest>();
  connected = new BehaviorSubject<boolean>(false);
  connectedToChannel = new BehaviorSubject<
    | {
        channelId: string;
        connected: true;
      }
    | undefined
  >(undefined);

  connecting = new BehaviorSubject<boolean>(false);
  connectionError = new BehaviorSubject<
    | {
        message: string;
      }
    | undefined
  >(undefined);

  /**
   * Pong socket to make alive.
   */
  private sendPong(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          action: 'pong',
          channelId: '',
          from: '',
          message: '',
          topic: '',
          id: ''
        })
      );
    }
  }

  /**
   * Subscribe channel.
   *
   * @param {string} channelId Channel id.
   * @param {boolean} forcedToConnect Boolean.
   * @returns {Observable<boolean>}
   */
  subscribeChannel(
    channelId: string,
    forcedToConnect?: boolean
  ): Observable<boolean> {
    return defer(() => {
      const message: WSChatMessageRequest = {
        action: 'subscribe',
        channelId: channelId,
        from: this.session.getProfile()?.id ?? '',
        message: 'Joined the channel',
        topic: `CHAT-${channelId}`
      };

      return of(true).pipe(
        switchMap(() => {
          if (
            !this.ws ||
            (this.ws.readyState !== WebSocket.OPEN &&
              this.ws.readyState !== WebSocket.CONNECTING) ||
            forcedToConnect
          ) {
            return this.connect().pipe(map(() => true));
          }
          return of(true);
        }),
        switchMap(() => this.send(message))
      );
    });
  }

  /**
   * Unsubscribe channel.
   *
   * @param {string} channelId ChannelId.
   * @returns {Observable<boolean>}
   */
  unsubscribeChannel(channelId: string): Observable<boolean> {
    return defer(() => {
      const message: WSChatMessageRequest = {
        action: 'unsubscribe',
        channelId: channelId,
        from: this.session.getProfile()?.id ?? '',
        message: 'Leave the channel',
        topic: `CHAT-${channelId}`
      };

      return of(true).pipe(
        switchMap(() => {
          if (
            this.ws &&
            (this.ws.readyState === WebSocket.CONNECTING ||
              this.ws.readyState === WebSocket.OPEN)
          ) {
            const ws = this.ws;
            return iif(
              () => ws.readyState === WebSocket.CONNECTING,
              of(true).pipe(delay(1000)),
              of(true)
            ).pipe(
              switchMap(() => this.send(message)),
              delay(300)
            );
          }
          return of(true);
        })
      );
    });
  }

  /**
   * Close subscription.
   */
  close(): void {
    if (this.ws) {
      this.ws.onerror = null;
      this.ws.onclose = null;

      this.connected.next(false);
      this.connecting.next(false);
      this.connectedToChannel.next(undefined);
      this.ws.close(1000, 'Client close the connection');
    }
  }

  /**
   * Connect websocket.
   *
   * @param {boolean} forcedToConnect Boolean.
   * @returns {Observable<boolean>}
   */
  connect(forcedToConnect?: boolean): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      this.connecting.next(true);

      this.connectionError.next(undefined);

      const url = environment.API_CHAT.replace('https', 'wss')
        .replace('http', 'wss')
        .concat('/ws');

      const urlQueryParams = [];
      if (this.session.token) {
        urlQueryParams.push(`token=${this.session.token}`);
      }

      const wsUrl =
        urlQueryParams.length > 0 ? `${url}?${urlQueryParams.join('&')}` : url;

      if (
        this.ws === null ||
        this.ws.readyState === WebSocket.CLOSED ||
        this.ws.readyState === WebSocket.CLOSING ||
        forcedToConnect
      ) {
        const ws = new WebSocket(wsUrl);
        this.ws = ws;
      }

      if (this.ws.readyState === WebSocket.OPEN) {
        subscriber.next(true); // notify close
        subscriber.complete(); // notify close
      }
      /**
       * On websocket opened.
       */
      this.ws.onopen = () => {
        this.connected.next(true);
        this.connecting.next(false);

        subscriber.next(true); // notify close
        subscriber.complete(); // notify close
      };

      /**
       * On websocket received a message.
       *
       * @param {MessageEvent} event MessageEvent.
       */
      this.ws.onmessage = (event: MessageEvent) => {
        const data: WSChatMessageRequest = JSON.parse(event.data);

        if (typeof data.system === 'string') {
          if (data.system.includes('Subscribed to')) {
            const firstDashIndex = data.system.indexOf('-');
            const channelId = data.system.substring(firstDashIndex + 1);
            if (channelId) {
              this.connectedToChannel.next({
                channelId: channelId,
                connected: true
              });
            }
          }
        } else if (data.from === 'system') {
          if (
            /quota is empty/i.test(data.message) ||
            /daily quota is expired/i.test(data.message)
          ) {
            this.channelRunOutQuota.next();
          }
        } else {
          this.channelChatReceived.next(data as any as Chat);
        }

        if (data.type === 'ping') {
          this.sendPong();
        }

        this.wsMessageReceived.next(data);
      };

      /**
       * On websocket throw an error.
       *
       * @param {Event} event Event.
       */
      this.ws.onerror = (event) => {
        this.connectionError.next({ message: 'Failed to connect' });
        this.connectedToChannel.next(undefined);
        this.connected.next(false);
        this.connecting.next(false);
        subscriber.error(event); // notify error
        subscriber.complete(); // notify close
      };

      /**
       * On websocket closed.
       */
      this.ws.onclose = () => {
        this.connected.next(false);
        this.connecting.next(false);
        this.connectedToChannel.next(undefined);
        subscriber.complete(); // notify close
      };

      // Clean up on unsubscribe
      return () => true;
    });
  }

  /**
   * Send message as Observable. Will error if WebSocket is not open or fails.
   *
   * @param {WSChatMessageRequest} message WSChatMessageRequest.
   * @returns {Observable<void>} Observable that completes or errors.
   */
  send(message: WSChatMessageRequest): Observable<boolean> {
    return defer(() => {
      if (
        !this.ws ||
        (this.ws.readyState !== WebSocket.OPEN &&
          this.ws.readyState !== WebSocket.CONNECTING)
      ) {
        return throwError(() => new Error('WebSocket is not connected.'));
      }

      const ws = this.ws;

      return of(true).pipe(
        switchMap(() =>
          iif(
            () => ws.readyState === WebSocket.CONNECTING,
            of(true).pipe(delay(1000)),
            of(true)
          )
        ),

        switchMap(() =>
          of(true).pipe(
            map(() => {
              if (ws.readyState !== WebSocket.OPEN) {
                throw new Error(`Failed to connect to the server.`);
              }
              ws.send(JSON.stringify(message));
              return true;
            })
          )
        )
      );
    });
  }
}
